import { useState, useCallback } from 'react';
import Layout from '../components/layout/Layout';
import { AccessCodeGate } from '../components/sales-demo/AccessCodeGate';
import { JobContextCard } from '../components/sales-demo/JobContextCard';
import { VoiceSessionCard } from '../components/sales-demo/VoiceSessionCard';
import { InterviewActiveCard } from '../components/sales-demo/InterviewActiveCard';
import { SessionResults } from '../components/sales-demo/SessionResults';
import { evaluateSession, type SalesEvaluation } from '../services/salesDemoService';
import type { ConversationTurn, SessionStatus } from '../hooks/useVoiceSession';

export function SalesDemoPage() {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [sessionActive, setSessionActive] = useState(false);

  const [evaluation, setEvaluation] = useState<SalesEvaluation | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);

  const handleStatusChange = useCallback((status: SessionStatus) => {
    setSessionActive(status === 'active' || status === 'connecting' || status === 'ending');
    if (status === 'connecting') {
      setEvaluation(null);
      setEvalError(null);
    }
  }, []);

  const handleSessionEnd = useCallback(
    async (sessionTranscript: ConversationTurn[], _audioBlob: Blob | null) => {
      if (sessionTranscript.length < 2) {
        setEvalError('Interview too short to evaluate — please try again.');
        return;
      }

      setEvaluating(true);
      setEvalError(null);
      try {
        const result = await evaluateSession(sessionTranscript, jobDescription, accessCode);
        setEvaluation(result.evaluation);
      } catch (err) {
        setEvalError(
          err instanceof Error ? err.message : 'Could not generate evaluation.',
        );
      } finally {
        setEvaluating(false);
      }
    },
    [jobDescription, accessCode],
  );

  const handleReset = useCallback(() => {
    setJobDescription('');
    setEvaluation(null);
    setEvalError(null);
    setEvaluating(false);
    setSessionActive(false);
  }, []);

  if (!isUnlocked) {
    return (
      <Layout hideFooter>
        <div className="min-h-[calc(100vh-64px)] bg-[var(--color-background-gray)]">
          <AccessCodeGate
            onSuccess={(code) => {
              setAccessCode(code);
              setIsUnlocked(true);
            }}
          />
        </div>
      </Layout>
    );
  }

  const showResults = evaluation && !evaluating;

  // Right card state machine
  const renderRightCard = () => {
    if (showResults) {
      return <SessionResults evaluation={evaluation} />;
    }

    if (evaluating) {
      return (
        <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col p-5 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gray-100 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
              <div className="h-2 w-16 rounded bg-gray-50 animate-pulse" />
            </div>
          </div>
          <div className="space-y-2 flex-1">
            <div className="h-2.5 w-full rounded bg-gray-100 animate-pulse" />
            <div className="h-2.5 w-4/5 rounded bg-gray-100 animate-pulse" />
            <div className="h-2.5 w-3/5 rounded bg-gray-50 animate-pulse" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="w-36 h-36 rounded-full border-2 border-gray-100 animate-pulse" />
          </div>
          <p className="font-body text-dark-invirtus/30 text-[11px] text-center">
            Analyzing your interview...
          </p>
        </div>
      );
    }

    if (evalError) {
      return (
        <div className="bg-white rounded-2xl border border-gray-100 h-full flex flex-col items-center justify-center gap-4 px-6">
          <div className="p-4 rounded-xl bg-[var(--color-light-red)] text-[var(--color-red)] text-sm text-center">
            {evalError}
          </div>
        </div>
      );
    }

    // During active session — show interview tips instead of job description
    if (sessionActive) {
      return <InterviewActiveCard />;
    }

    // Default — job description input
    return (
      <JobContextCard
        jobDescription={jobDescription}
        onJobDescriptionChange={setJobDescription}
        disabled={sessionActive}
      />
    );
  };

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-64px)] bg-[var(--color-background-gray)]">
        <div className="px-6 lg:px-10 py-8">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <h1 className="font-heading text-dark-invirtus text-xl font-bold tracking-tight">
                Sales Closer Evaluation
              </h1>
              <p className="font-body text-dark-invirtus/40 text-sm mt-1">
                AI-powered sales interview assessment
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 h-[520px]">
              <div className="lg:col-span-3 h-full">
                <VoiceSessionCard
                  jobDescription={jobDescription}
                  accessCode={accessCode}
                  onStatusChange={handleStatusChange}
                  onSessionEnd={handleSessionEnd}
                  onReset={handleReset}
                />
              </div>
              <div className="lg:col-span-2 h-full">
                {renderRightCard()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
